import React from "react";
import { Link, NavLink, Route, Routes, useNavigate, useParams } from "react-router-dom";
import {
  MdArrowBack,
  MdDashboard,
  MdCampaign,
  MdFolderOpen,
  MdQuiz,
  MdSettings,
  MdRefresh,
  MdErrorOutline,
  MdPeopleAlt,
  MdInsights,
  MdOndemandVideo,
  MdChat,
} from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { instructorCoursesService, type InstructorCourse } from "services/instructorCoursesService";
import { CourseProvider, type CourseCounts, type CourseContextValue } from "../context/CourseContext";
import OverviewPage from "./overview/OverviewPage";
import AnnouncementsPage from "./announcements/AnnouncementsPage";
import ContentPage from "./content/ContentPage";
import QuizzesPage from "./quizzes/QuizzesPage";
import QuizEditorPage from "./quizzes/QuizEditorPage";
import SettingsPage from "./settings/SettingsPage";
import StudentProgressPage from "./student-progress/StudentProgressPage";
import VideoPlayerSettingsPage from "./video-player-settings/VideoPlayerSettingsPage";
import ChatPage from "./chat/ChatPage";
import { Skeleton } from "components/ui/Skeleton";
import { resolveStoredUploadPreview } from "lib/storedUploadImage";

type NavEntry = {
  to: string;
  labelAr: string;
  labelEn: string;
  icon: React.ReactNode;
  end?: boolean;
};

const NAV: NavEntry[] = [
  { to: "overview", labelAr: "نظرة عامة", labelEn: "Overview", icon: <MdDashboard className="h-5 w-5" />, end: true },
  { to: "announcements", labelAr: "الإعلانات", labelEn: "Announcements", icon: <MdCampaign className="h-5 w-5" /> },
  { to: "content", labelAr: "المحتوى", labelEn: "Content", icon: <MdFolderOpen className="h-5 w-5" /> },
  { to: "quizzes", labelAr: "الاختبارات", labelEn: "Quizzes", icon: <MdQuiz className="h-5 w-5" /> },
  { to: "student-progress", labelAr: "تقدم الطلاب", labelEn: "Student Progress", icon: <MdInsights className="h-5 w-5" /> },
  { to: "video-player-settings", labelAr: "إعدادات مشغل الفيديو", labelEn: "Video Player Settings", icon: <MdOndemandVideo className="h-5 w-5" /> },
  { to: "chat", labelAr: "المحادثة", labelEn: "Chat", icon: <MdChat className="h-5 w-5" /> },
  { to: "settings", labelAr: "الإعدادات", labelEn: "Settings", icon: <MdSettings className="h-5 w-5" /> },
];

export default function CourseWorkspaceLayout() {
  const { courseId } = useParams();
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();

  const [course, setCourse] = React.useState<InstructorCourse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [counts, setCountsState] = React.useState<CourseCounts>({});

  const reload = React.useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const data = await instructorCoursesService.get(courseId);
      if (!data) setNotFound(true);
      else setCourse(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const setCounts = React.useCallback((next: Partial<CourseCounts>) => {
    setCountsState((prev) => ({ ...prev, ...next }));
  }, []);

  if (loading && !course) {
    return (
      <div className="flex flex-col gap-4 p-2">
        <Skeleton className="h-40 w-full" />
        <div className="grid grid-cols-12 gap-4">
          <Skeleton className="col-span-12 h-80 md:col-span-3" />
          <Skeleton className="col-span-12 h-80 md:col-span-9" />
        </div>
      </div>
    );
  }

  if (notFound || !course) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <MdErrorOutline className="h-10 w-10 text-red-500" />
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">
          {isArabic ? "لم يتم العثور على الكورس" : "Course not found"}
        </h2>
        <p className="text-sm text-gray-500">
          {isArabic
            ? "قد يكون الكورس حُذف أو لا تملك صلاحية لعرضه."
            : "It may have been deleted or you don't have access to it."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/instructor/courses")}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
        >
          <MdArrowBack className={isArabic ? "rotate-180" : ""} />
          {isArabic ? "العودة إلى الكورسات" : "Back to courses"}
        </button>
      </div>
    );
  }

  const value: CourseContextValue = { course, reload, counts, setCounts };
  const courseImageSrc = resolveStoredUploadPreview(course.image, course.image_url);

  return (
    <CourseProvider value={value}>
      <div className="flex flex-col gap-4 p-2">
        <header className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center">
            <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl md:h-28 md:w-28">
              {courseImageSrc ? (
                <img
                  src={courseImageSrc}
                  alt={course.title}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback instanceof HTMLElement) fallback.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-400 to-brand-600 text-2xl font-bold uppercase text-white"
                style={{ display: courseImageSrc ? "none" : "flex" }}
              >
                {course.title.slice(0, 2)}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/instructor/courses"
                  className="inline-flex items-center gap-1 text-xs text-brand-500 hover:underline"
                >
                  <MdArrowBack className={isArabic ? "h-4 w-4 rotate-180" : "h-4 w-4"} />
                  {isArabic ? "الكورسات" : "Courses"}
                </Link>
                {course.subject ? (
                  <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-200">
                    {(course.subject as any)?.name ?? (course.subject as any)?.title ?? ""}
                  </span>
                ) : null}
              </div>
              <h1 className="mt-1 truncate text-xl font-bold text-gray-800 dark:text-white md:text-2xl">
                {course.title}
              </h1>
              {course.description ? (
                <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-300">
                  {course.description}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={reload}
                className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
              >
                <MdRefresh className="h-4 w-4" />
                {isArabic ? "تحديث" : "Refresh"}
              </button>
              <Link
                to={`/instructor/students?course_id=${course.id}`}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600"
                title={isArabic ? "الطلاب" : "Students"}
              >
                <MdPeopleAlt className="h-4 w-4" />
                {isArabic ? "الطلاب" : "Students"}
              </Link>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-12 gap-4">
          <aside className="col-span-12 md:col-span-3 xl:col-span-2">
            <nav className="sticky top-20 flex flex-row overflow-x-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-sm dark:border-white/10 dark:bg-navy-800 md:flex-col md:gap-1 md:overflow-visible">
              {NAV.map((entry) => (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.end}
                  className={({ isActive }) =>
                    `flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors md:shrink ${
                      isActive
                        ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
                    }`
                  }
                >
                  {entry.icon}
                  <span className="whitespace-nowrap">{isArabic ? entry.labelAr : entry.labelEn}</span>
                </NavLink>
              ))}
            </nav>
          </aside>

          <section className="col-span-12 min-w-0 md:col-span-9 xl:col-span-10">
            <Routes>
              <Route index element={<OverviewPage />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="content" element={<ContentPage />} />
              <Route path="content/folders/:folderId" element={<ContentPage />} />
              <Route path="quizzes" element={<QuizzesPage />} />
              <Route path="quizzes/:quizId" element={<QuizEditorPage />} />
              <Route path="student-progress" element={<StudentProgressPage />} />
              <Route path="video-player-settings" element={<VideoPlayerSettingsPage />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Routes>
          </section>
        </div>
      </div>
    </CourseProvider>
  );
}
